import { useEffect, useState } from "react";
import { useSearchParams, Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * VendorLogin — one-time magic-link consumption page.
 * Vendor lands here from the welcome email or the link the admin shared. We
 * POST the token to /api/auth/magic-link/consume, drop the returned JWT into
 * AuthContext, and redirect to /vendor (their dashboard, which we'll build
 * out in Phase C).
 */
export default function VendorLogin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "No token supplied. Ask an admin for a fresh link." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/consume", { token });
        localStorage.setItem("jx_token", data.token);
        setUser(data.user);
        toast.success(`Welcome to Jeweltix, ${data.user.name}!`);
        setState({ status: "ok", message: "You're logged in. Redirecting…" });
        // small delay so the user sees the check
        setTimeout(() => { window.location.assign("/vendor"); }, 900);
      } catch (err) {
        const msg = err?.response?.data?.detail || "This link is invalid or expired. Ask an admin for a fresh one.";
        setState({ status: "error", message: msg });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="vendor-login-page">
      <div className="max-w-md w-full border border-border rounded-lg bg-card p-8 text-center">
        {state.status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
            <h1 className="font-display font-semibold text-lg mt-4">Signing you in…</h1>
            <p className="text-sm text-muted-foreground mt-1">Verifying your magic link.</p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
            <h1 className="font-display font-bold text-lg mt-4">Signed in!</h1>
            <p className="text-sm text-muted-foreground mt-1">{state.message}</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="font-display font-bold text-lg mt-4">Link couldn't be used</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">{state.message}</p>
            <Link to="/vendor/apply" className="mt-6 inline-block text-sm text-primary hover:underline">
              Reapply for CAM vendor access →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
